// Partner referrals — one cross-tenant campaign primitive, two shapes.
//   A. Source sends (default): the source tenant emails its own segment from its
//      own identity; only people who click/reply become leads on the partner.
//   B. Audience transfer: name/email/address move to the partner as a one-shot
//      PartnerAudience (never customers); the partner sends, disclosing it.
// Every action here hits /api/partner/*; the server owns every gate (owner-only
// grant, expiry, policy attestation, CAN-SPAM compliance, approval, test-send
// allow-list). This page only renders what the server says.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../apiClient';
import { listTemplates } from '../../api/templatesApi';
import UiBadge from '../../components/ui/badge';
import RevivalHeader from '../../components/Revival/RevivalHeader';
import '../../components/Revival/revival.css';
import './sales-ds.css';
import './PartnerProspects.css';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const err = (e, fallback) => e?.response?.data?.detail || e?.response?.data?.error || e?.message || fallback;

const DEFAULT_BODY = `Hi {{first_name}},

{{partner_disclosure}}

Here's the offer: {{partner_offer_url}}

Thanks!`;

const SHAPE_LABEL = { source_sends: 'A · source sends', audience_transfer: 'B · audience transfer' };
const PURPOSE_FOR_SHAPE = { source_sends: 'partner_send', audience_transfer: 'audience_transfer' };

export default function PartnerProspects() {
  const [auths, setAuths] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flash, setFlash] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, c, r, d] = await Promise.all([
        api.get('/partner/authorizations/'),
        api.get('/partner/campaigns/'),
        api.get('/partner/referrals/'),
        api.get('/partner/audiences/'),
      ]);
      setAuths(a.data?.authorizations || []);
      setCampaigns(c.data?.campaigns || []);
      setReferrals(r.data?.referrals || []);
      setAudiences(d.data?.audiences || []);
      setError('');
    } catch (e) {
      setError(err(e, 'Could not load partner prospects.'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const say = (msg) => { setFlash(msg); setError(''); };
  const fail = (e, fallback) => { setError(err(e, fallback)); setFlash(''); };

  return (
    <div className="rv-screen pr-page">
      <RevivalHeader title="Partner prospects" totals={loading ? ['Loading…'] : []} />
      <p className="rv-note">A partner's audience, worked as prospects: email-only, kept separate from your customers. Anyone who responds becomes a lead with partner attribution; everyone else stays a prospect until the list expires.</p>
      {error ? <div className="pr-error">{error} <button className="pr-link" onClick={load}>Retry</button></div> : null}
      {flash ? <div className="pr-flash">{flash}</div> : null}

      <GrantSection auths={auths} onDone={(m) => { say(m); load(); }} onError={fail} />
      <CampaignSection auths={auths} campaigns={campaigns} onDone={(m) => { say(m); load(); }} onError={fail} />
      <RespondersSection referrals={referrals} onDone={(m) => { say(m); load(); }} onError={fail} />
      <ProspectListSection audiences={audiences} />
    </div>
  );
}

/* ── 1. Authorization (source tenant owner) ─────────────────────────────── */
function GrantSection({ auths, onDone, onError }) {
  const [open, setOpen] = useState(false);
  const [shape, setShape] = useState('source_sends');
  const [targetId, setTargetId] = useState('');
  const [filterText, setFilterText] = useState('{}');
  const [expires, setExpires] = useState('');
  const [note, setNote] = useState('');
  const [attest, setAttest] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const parsedFilter = useMemo(() => { try { return JSON.parse(filterText || '{}'); } catch { return null; } }, [filterText]);

  const doPreview = async () => {
    if (parsedFilter === null) return onError({ message: 'Segment filter is not valid JSON.' });
    setBusy(true);
    try {
      const r = await api.post('/partner/authorizations/preview/', { segment_filter: parsedFilter });
      setPreview(r.data);
    } catch (e) { onError(e, 'Preview failed.'); } finally { setBusy(false); }
  };

  const grant = async () => {
    if (parsedFilter === null) return onError({ message: 'Segment filter is not valid JSON.' });
    setBusy(true);
    try {
      await api.post('/partner/authorizations/', {
        purpose: PURPOSE_FOR_SHAPE[shape], target_tenant_id: targetId, segment_filter: parsedFilter,
        expires_at: expires ? new Date(expires).toISOString() : undefined, note, policy_attestation: attest,
      });
      setOpen(false); setPreview(null); setAttest(''); setNote('');
      onDone('Authorization recorded.');
    } catch (e) { onError(e, 'Grant refused.'); } finally { setBusy(false); }
  };

  return (
    <section className="pr-section">
      <div className="pr-section-head">
        <h3>Authorizations</h3>
        <button className="pr-btn" onClick={() => setOpen((v) => !v)}>{open ? 'Close' : 'Authorize sharing'}</button>
      </div>
      {open ? (
        <div className="pr-form">
          <p className="pr-muted">You are granting as the <strong>source</strong> tenant (the current workspace). Only its owner or admin can grant; the server checks.</p>
          <label>Shape
            <select value={shape} onChange={(e) => setShape(e.target.value)}>
              <option value="source_sends">A · We send, partner gets responders (default)</option>
              <option value="audience_transfer">B · Transfer name/email/address to partner, partner sends</option>
            </select>
          </label>
          <label>Partner tenant ID<input value={targetId} onChange={(e) => setTargetId(e.target.value)} placeholder="e.g. 6" /></label>
          <label>Segment filter (same JSON the customer outreach composer uses)
            <textarea rows={3} value={filterText} onChange={(e) => setFilterText(e.target.value)} placeholder='{"derived": ["customer"], "quote_status": ""}' />
          </label>
          <div className="pr-row">
            <button className="pr-btn pr-btn-ghost" onClick={doPreview} disabled={busy}>Preview segment</button>
            {preview ? (
              <span className="pr-muted">
                <strong>{preview.sendable}</strong> sendable · excluded: {Object.entries(preview.exclusions || {}).map(([k, v]) => `${k} ${v}`).join(', ') || 'none'}
                <br />Fields that cross: {(preview.fields_that_cross || []).join(', ')} — never phone, notes or history.
              </span>
            ) : null}
          </div>
          <label>Expires<input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} /> <span className="pr-muted">default 30 days</span></label>
          <label>Note<input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Who approved, and for what" /></label>
          {shape === 'audience_transfer' ? (
            <label className="pr-attest">Privacy-policy attestation (required for shape B)
              <textarea rows={3} value={attest} onChange={(e) => setAttest(e.target.value)}
                placeholder="e.g. Privacy policy §6 updated on … to permit sharing with named partners; approved in writing by …" />
              <span className="pr-warn">A policy that says "we do not share your personal information for marketing purposes" blocks shape B until it is updated. Shape A needs no policy change.</span>
            </label>
          ) : null}
          <div className="pr-row">
            <button className="pr-btn" onClick={grant} disabled={busy || !targetId}>Record authorization</button>
          </div>
        </div>
      ) : null}
      <Table
        cols={['Purpose', 'Source → Partner', 'Segment', 'Authorized by', 'Expires', 'Status']}
        rows={auths.map((a) => [
          a.purpose.replace('_', ' '),
          `${a.source_tenant?.name} → ${a.target_tenant?.name || '—'}`,
          `${a.segment_count_at_grant} at grant`,
          `${a.authorized_by?.email} (${a.authorized_by?.role})`,
          fmtDate(a.expires_at),
          <Badge key="s" kind={a.valid ? 'ok' : 'neg'}>{a.valid ? 'valid' : (a.problem || 'invalid').replace(/_/g, ' ')}</Badge>,
        ])}
        empty="No authorizations yet."
      />
    </section>
  );
}

/* ── 2. Campaigns ───────────────────────────────────────────────────────── */
function CampaignSection({ auths, campaigns, onDone, onError }) {
  const [open, setOpen] = useState(false);
  const usable = auths.filter((a) => a.valid && a.purpose !== 'audience_export');
  const [authId, setAuthId] = useState('');
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState(DEFAULT_BODY);
  const [offerUrl, setOfferUrl] = useState('');
  const [disclosure, setDisclosure] = useState('');
  const [partnerKey, setPartnerKey] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!open) return;
    listTemplates({ channel: 'email' }).then((r) => {
      const rows = Array.isArray(r.data) ? r.data : (r.data?.results || r.data?.templates || []);
      setTemplates(rows.filter((t) => (t.channel || 'email') === 'email'));
    }).catch(() => setTemplates([]));
  }, [open]);

  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post('/partner/campaigns/', {
        authorization_id: authId, name, subject: templateId ? '' : subject, body: templateId ? '' : body,
        offer_url: offerUrl, partner_disclosure: disclosure, template_id: templateId || undefined, partner_key: partnerKey || undefined,
      });
      setDetail(r.data); setOpen(false);
      onDone(r.data?.compliance?.ok ? 'Campaign created — compliance clean. Approve, test-send, then send.' : 'Campaign created with compliance problems — see below.');
    } catch (e) { onError(e, 'Create refused.'); } finally { setBusy(false); }
  };
  const openDetail = useCallback(async (id) => {
    try { const r = await api.get(`/partner/campaigns/${id}/`); setDetail(r.data); } catch (e) { onError(e, 'Could not load campaign.'); }
  }, [onError]);
  const act = async (id, action, payload = {}) => {
    setBusy(true);
    try {
      const r = await api.post(`/partner/campaigns/${id}/${action}/`, payload);
      if (action === 'send' && payload.test_send) onDone(r.data?.status === 'test_sent' ? `Test sent from ${r.data.from_address}.` : `Test failed (Mailgun ${r.data?.mailgun_status}).`);
      else if (action === 'send') {
        setDetail((d) => (d && d.id === id ? { ...d, status: 'sending', resumable: false } : d));
        onDone(r.data?.status === 'resumed' ? `Resumed from chunk ${r.data.chunk} — ${r.data.sent} already sent.` : `Queued to ${r.data?.recipients} recipients — sending in chunks on the outreach lane.`);
        openDetail(id);
      }
      else onDone('Approved.');
      if (r.data?.id) setDetail(r.data);
    } catch (e) { onError(e, `${action} refused.`); } finally { setBusy(false); }
  };
  // The newest campaign opens by itself — this is the screen the founder sends from.
  useEffect(() => { if (campaigns.length && !detail) openDetail(campaigns[0].id); }, [campaigns, detail, openDetail]);
  const saveDisclosure = async (id, text) => {
    setBusy(true);
    try { const r = await api.patch(`/partner/campaigns/${id}/`, { partner_disclosure: text }); setDetail(r.data); onDone('Disclosure saved — back to draft; approve again to send.'); }
    catch (e) { onError(e, 'Save refused.'); } finally { setBusy(false); }
  };
  const refreshTemplate = async (id) => {
    setBusy(true);
    try { const r = await api.post(`/partner/campaigns/${id}/refresh-template/`, {}); setDetail(r.data); onDone('Refreshed from the saved template — back to draft; approve again to send.'); }
    catch (e) { onError(e, 'Refresh refused.'); } finally { setBusy(false); }
  };

  return (
    <section className="pr-section">
      <div className="pr-section-head">
        <h3>Partner campaigns</h3>
        {usable.length ? <button className="pr-btn" onClick={() => setOpen((v) => !v)}>{open ? 'Close' : 'New campaign'}</button> : null}
      </div>
      {!usable.length ? <p className="pr-muted">A valid authorization is needed first.</p> : null}
      {open ? (
        <div className="pr-form">
          <label>Authorization
            <select value={authId} onChange={(e) => setAuthId(e.target.value)}>
              <option value="">Choose…</option>
              {usable.map((a) => <option key={a.id} value={a.id}>{a.purpose === 'partner_send' ? 'A' : 'B'} · {a.source_tenant?.name} → {a.target_tenant?.name} · expires {fmtDate(a.expires_at)}</option>)}
            </select>
          </label>
          <label>Name<input value={name} onChange={(e) => setName(e.target.value)} placeholder="Free walk-through — Sun Pest customers" /></label>
          <label>Email template (your saved templates; the partnership disclosure and unsubscribe are added automatically)
            <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
              <option value="">Write it here instead…</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
          {!templateId ? (
            <>
              <label>Subject<input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="{{first_name}}, a free walk-through" /></label>
              <label>Body — <code>{'{{first_name}}'}</code>, <code>{'{{partner_disclosure}}'}</code>, <code>{'{{partner_offer_url}}'}</code>, <code>{'{{business_address}}'}</code> optional
                <textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)} />
              </label>
            </>
          ) : null}
          <label>Offer URL (only if the body carries <code>{'{{partner_offer_url}}'}</code>; otherwise people respond by replying)<input value={offerUrl} onChange={(e) => setOfferUrl(e.target.value)} placeholder="https://…" /></label>
          <label>Partnership disclosure (leave blank for the default sentence)<input value={disclosure} onChange={(e) => setDisclosure(e.target.value)} /></label>
          <label>Attribution key (leads show as partner:&lt;key&gt;)<input value={partnerKey} onChange={(e) => setPartnerKey(e.target.value)} placeholder="sunpest" /></label>
          <div className="pr-row"><button className="pr-btn" onClick={create} disabled={busy || !authId || !name || (!templateId && !subject)}>Create draft</button></div>
        </div>
      ) : null}
      <Table
        cols={['Campaign', 'Shape', 'Sends as', 'Status', 'Engagement (Mailgun)', 'Responders', '']}
        rows={campaigns.map((c) => [
          <button key="n" className="pr-link" onClick={() => openDetail(c.id)}>{c.name}<div className="pr-sub">{fmtDate(c.created_at)}</div></button>,
          SHAPE_LABEL[c.shape] || c.shape,
          c.sending_tenant?.name,
          <Badge key="s" kind={c.status === 'completed' ? 'ok' : c.status === 'failed' ? 'neg' : 'accent'}>{c.status}</Badge>,
          <span key="e" className="pr-sub" title={c.engagement?.line}>{c.engagement?.line || (c.run_summary?.sent != null ? `${c.run_summary.sent} sent` : '—')}</span>,
          `${c.responders?.converted || 0} leads · ${c.responders?.pending || 0} pending`,
          <span key="a" className="pr-actions">
            {c.status === 'draft' ? <button className="pr-btn pr-btn-sm" disabled={busy} onClick={() => act(c.id, 'approve')}>Approve</button> : null}
            {c.status === 'approved' ? <button className="pr-btn pr-btn-sm pr-btn-ghost" disabled={busy} onClick={() => act(c.id, 'send', { test_send: true })}>Test to khalis@abon.ai</button> : null}
            {c.status === 'approved' ? <button className="pr-btn pr-btn-sm" disabled={busy} onClick={() => act(c.id, 'send', { confirmed: true })}>Send</button> : null}
          </span>,
        ])}
        empty="No partner campaigns yet."
      />
      {detail ? <CampaignDetail data={detail} busy={busy} onClose={() => setDetail(null)} onAct={(action, payload) => act(detail.id, action, payload)} onRefresh={() => refreshTemplate(detail.id)} onSaveDisclosure={(t) => saveDisclosure(detail.id, t)} onReload={() => openDetail(detail.id)} /> : null}
    </section>
  );
}

function CampaignDetail({ data, busy, onClose, onAct, onRefresh, onSaveDisclosure, onReload }) {
  const c = data.compliance;
  const rs = data.run_summary || {};
  // While the outreach lane works through the chunks, refresh so the founder
  // watches sent/failed climb instead of a frozen 'sending'.
  useEffect(() => {
    if (data.status !== 'sending' || !onReload) return undefined;
    const t = setInterval(onReload, 15000);
    return () => clearInterval(t);
  }, [data.status, data.id, onReload]);
  const [disc, setDisc] = useState(data.partner_disclosure || '');
  useEffect(() => { setDisc(data.partner_disclosure || ''); }, [data.id, data.partner_disclosure]);
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState('');
  useEffect(() => {
    let alive = true;
    setPreview(null); setPreviewErr('');
    api.get(`/partner/campaigns/${data.id}/preview/`).then((r) => { if (alive) setPreview(r.data); }).catch((e) => { if (alive) setPreviewErr(err(e, 'Preview failed.')); });
    return () => { alive = false; };
  }, [data.id, data.body]);
  const sendable = data.status === 'approved' && c?.ok;
  return (
    <div className="pr-detail">
      <div className="pr-section-head">
        <h4>{data.name}</h4>
        <span className="pr-actions">
          {data.template_id && data.status !== 'completed' && data.status !== 'sending' ? <button className="pr-btn pr-btn-sm pr-btn-ghost" disabled={busy} onClick={onRefresh}>Refresh from template</button> : null}
          {data.status === 'draft' ? <button className="pr-btn pr-btn-sm" disabled={busy} onClick={() => onAct('approve')}>Approve</button> : null}
          {data.status === 'approved' ? <button className="pr-btn pr-btn-sm pr-btn-ghost" disabled={busy} onClick={() => onAct('send', { test_send: true })}>Test to khalis@abon.ai</button> : null}
          {sendable ? <SendButton count={c?.recipients} sender={data.sending_tenant?.name} busy={busy} onSend={() => onAct('send', { confirmed: true })} /> : null}
          {data.resumable ? <button className="pr-btn pr-btn-sm" disabled={busy} onClick={() => onAct('send', { confirmed: true })} title="No chunk has reported in for 10 minutes. Resume enqueues the next chunk; already-attempted prospects are never sent twice.">Resume send</button> : null}
          <button className="pr-link" onClick={onClose}>Close</button>
        </span>
      </div>
      <div className="pr-kv">
        <div><span>Shape</span>{SHAPE_LABEL[data.shape]}</div>
        <div><span>Sends as</span>{data.sending_tenant?.name} {c?.from_address ? `· ${c.from_address}` : ''}</div>
        <div><span>Partner</span>{data.source_tenant?.name} → {data.target_tenant?.name}</div>
        <div><span>Attribution</span>{data.attribution}</div>
        {data.engagement?.line ? <div><span>Engagement (Mailgun events)</span>{data.engagement.line}</div> : null}
        {data.status === 'sending' || rs.chunks != null ? (
          <div><span>Progress</span>{rs.sent ?? 0} sent · {rs.failed ?? 0} failed · {rs.skipped ?? 0} skipped
            {data.status === 'sending' ? ` · chunk ${(rs.chunk ?? 0) + 1} · last report ${rs.last_chunk_at ? new Date(rs.last_chunk_at).toLocaleTimeString() : '—'}` : ` · ${rs.chunks} chunks`}
            {data.resumable ? <span className="pr-warn"> · stalled</span> : null}
          </div>
        ) : null}
        <div><span>Recipients</span>{c?.recipients ?? '—'}</div>
        <div><span>Postal address</span>{c?.physical_address || <span className="pr-warn">missing</span>}</div>
        <div style={{ gridColumn: '1 / -1' }}><span>Partnership line (in the email footer)</span>
          <div className="pr-row" style={{ marginTop: 4 }}>
            <input value={disc} onChange={(e) => setDisc(e.target.value)} style={{ flex: 1, minWidth: 260, font: 'inherit', fontSize: '0.84rem', color: 'var(--text-1)', background: 'var(--bg-1)', border: '1px solid var(--border-1)', borderRadius: 'var(--r-1)', padding: '6px 8px' }} />
            {disc !== (data.partner_disclosure || '') && data.status !== 'completed' && data.status !== 'sending' ? <button className="pr-btn pr-btn-sm" disabled={busy} onClick={() => onSaveDisclosure(disc)}>Save</button> : null}
          </div>
        </div>
        <div><span>Responders</span>{data.responders?.converted} leads · {data.responders?.pending} pending · {data.responders?.dismissed} dismissed</div>
        {data.audience ? <div><span>Audience</span>{data.audience.member_count} people · {data.audience.status} · expires {fmtDate(data.audience.expires_at)}</div> : null}
      </div>
      {c ? (
        <div className={`pr-compliance ${c.ok ? 'ok' : 'bad'}`}>
          {c.ok ? 'Compliance: unsubscribe link, postal address, partnership disclosure, valid authorization — all present.' : (
            <ul>{c.problems.map((p) => <li key={p.code}><strong>{p.code.replace(/_/g, ' ')}</strong> — {p.message}</li>)}</ul>
          )}
        </div>
      ) : null}
      <div className="pr-preview">
        <div className="pr-preview-head">
          <span><strong>Preview</strong> — exactly what {preview?.sample_recipient ? `${preview.sample_recipient} (first prospect)` : 'a prospect'} receives</span>
          {preview?.from_address ? <span className="pr-muted">From {preview.from_address}</span> : null}
        </div>
        {preview ? (
          <>
            <div className="pr-preview-subject">Subject: {preview.subject}</div>
            <iframe title="Email preview" className="pr-preview-frame" sandbox="" srcDoc={preview.html} />
          </>
        ) : previewErr ? <div className="pr-warn">{previewErr}</div> : <div className="pr-muted">Rendering…</div>}
      </div>
    </div>
  );
}

function SendButton({ count, sender, busy, onSend }) {
  const [arm, setArm] = useState(false);
  // The button guards ITSELF: one click fires once. `fired` never resets — a
  // second click on the same mount is impossible, and a re-render from a stale
  // list row cannot re-arm it. (A double-click reached the API on 2026-09-21.)
  const [fired, setFired] = useState(false);
  if (fired) return <span className="pr-muted">Sending… this button fired once and is done.</span>;
  if (!arm) return <button className="pr-btn pr-btn-sm pr-btn-send" disabled={busy} onClick={() => setArm(true)}>Send to {count} prospects</button>;
  return (
    <span className="pr-actions">
      <span className="pr-warn">Send {count} emails from {sender}? This cannot be undone.</span>
      <button className="pr-btn pr-btn-sm pr-btn-send" disabled={busy || fired} onClick={() => { if (fired) return; setFired(true); onSend(); }}>Yes, send now</button>
      <button className="pr-btn pr-btn-sm pr-btn-ghost" disabled={fired} onClick={() => setArm(false)}>Cancel</button>
    </span>
  );
}

/* ── 3. Responders (target tenant) ──────────────────────────────────────── */
function RespondersSection({ referrals, onDone, onError }) {
  const [busy, setBusy] = useState(false);
  const act = async (id, action) => {
    setBusy(true);
    try { await api.post(`/partner/referrals/${id}/${action}/`, {}); onDone(action === 'convert' ? 'Lead created.' : 'Dismissed.'); }
    catch (e) { onError(e, `${action} refused.`); } finally { setBusy(false); }
  };
  if (!referrals.length) return null;
  return (
    <section className="pr-section">
      <div className="pr-section-head"><h3>Responders</h3><span className="pr-muted">People who clicked or replied. Attribution: partner referral.</span></div>
      <Table
        cols={['Person', 'From', 'Campaign', 'Event', 'Status', '']}
        rows={referrals.map((r) => [
          <span key="p">{r.name || r.email}<div className="pr-sub">{r.email}{r.address ? ` · ${r.address}` : ''}</div></span>,
          r.source_tenant_name,
          r.campaign_name,
          `${r.first_event} · ${r.clicks} clicks · ${r.replies} replies`,
          <Badge key="s" kind={r.status === 'converted' ? 'ok' : r.status === 'dismissed' ? 'neg' : 'accent'}>{r.status}</Badge>,
          <span key="a" className="pr-actions">
            {r.status === 'pending' ? <button className="pr-btn pr-btn-sm" disabled={busy} onClick={() => act(r.id, 'convert')}>Make lead</button> : null}
            {r.status === 'pending' ? <button className="pr-btn pr-btn-sm pr-btn-ghost" disabled={busy} onClick={() => act(r.id, 'dismiss')}>Dismiss</button> : null}
            {r.lead_id ? <a className="pr-link" href={`/leads/${r.lead_id}`}>Open lead</a> : null}
          </span>,
        ])}
      />
    </section>
  );
}

/* ── 4. Prospect list (target tenant) ──────────────────────────────────── */
function ProspectListSection({ audiences }) {
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filter, setFilter] = useState('all');
  useEffect(() => {
    if (!openId) { setDetail(null); return; }
    api.get(`/partner/audiences/${openId}/`).then((r) => setDetail(r.data)).catch(() => setDetail(null));
  }, [openId]);
  if (!audiences.length) return null;
  const members = (detail?.members || []).filter((m) => filter === 'all' || m.status === filter);
  const counts = (detail?.members || []).reduce((acc, m) => { acc[m.status] = (acc[m.status] || 0) + 1; return acc; }, {});
  return (
    <section className="pr-section">
      <div className="pr-section-head"><h3>Prospect lists</h3><span className="pr-muted">Email-only. Not customers. Responders become leads; the rest stay here until the list expires.</span></div>
      <Table
        cols={['List', 'From', 'People', 'Sent', 'Unsubscribed', 'Expires', 'Status', '']}
        rows={audiences.map((a) => [a.name, a.source_tenant_name, a.member_count, a.sent, a.unsubscribed, fmtDate(a.expires_at),
          <Badge key="s" kind={a.status === 'active' ? 'accent' : 'neg'}>{a.status}</Badge>,
          <button key="o" className="pr-link" onClick={() => setOpenId(openId === a.id ? null : a.id)}>{openId === a.id ? 'Hide' : 'Open'}</button>])}
      />
      {detail ? (
        <div className="pr-detail">
          <div className="pr-section-head">
            <h4>{detail.name}</h4>
            <div className="pr-row">
              {['all', 'lead', 'responded', 'sent', 'unsubscribed', 'queued'].map((k) => (
                <button key={k} className={`pr-btn pr-btn-sm ${filter === k ? '' : 'pr-btn-ghost'}`} onClick={() => setFilter(k)}>
                  {k}{k !== 'all' ? ` ${counts[k] || 0}` : ` ${(detail.members || []).length}`}
                </button>
              ))}
            </div>
          </div>
          <Table
            cols={['Prospect', 'Address', 'Status', 'Sent', 'Responded', '']}
            rows={members.map((m) => [
              <span key="p">{m.name || m.email}<div className="pr-sub">{m.email}</div></span>,
              [m.address, m.city, m.state, m.zip_code].filter(Boolean).join(', '),
              <Badge key="s" kind={m.status === 'lead' ? 'ok' : m.status === 'unsubscribed' ? 'neg' : m.status === 'responded' ? 'accent' : 'muted'}>{m.status}</Badge>,
              fmtDate(m.sent_at), fmtDate(m.responded_at),
              m.lead_id ? <a key="l" className="pr-link" href={`/leads/${m.lead_id}`}>Open lead</a> : null,
            ])}
            empty="No prospects match."
          />
        </div>
      ) : null}
    </section>
  );
}

/* ── bits ───────────────────────────────────────────────────────────────── */
function Table({ cols, rows, empty }) {
  if (!rows.length) return empty ? <div className="pr-empty">{empty}</div> : null;
  return (
    <div className="ds-table-wrap">
      <table className="ds-table pr-table">
        <thead><tr>{cols.map((c, i) => <th key={i}>{c}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i}>{r.map((cell, j) => <td key={j}>{cell}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}
const BADGE_TONE = { ok: 'success', neg: 'danger', accent: 'info', muted: 'neutral' };
function Badge({ kind, children }) { return <UiBadge tone={BADGE_TONE[kind] || 'neutral'}>{children}</UiBadge>; }
